/* eslint-disable @typescript-eslint/naming-convention */
import CONSTANTS, { FIELD_TYPE, INVOICE_CARRIER_TYPES, INVOICE_DONATE_MARKS } from '../../../constants/Constants';
import {
  IAcceptedInstallments,
  IBopisInfoResponse,
  ICompanyBillingDetailsResponse, 
  IConvenienceStoreResponse,
  IInvoiceDetailsResponse,
  INotification,
  ITransPaymentResponse,
} from '../../IApiResponses';
import {
  GiftCard,
  ICreditCard,
  IFromBopisInfoResponse,
  IFromCompanyBillingDetailsResponse,
  IFromTransactionMapping,
  IInvoiceDetails,
} from '../../IDataMappings';
import { ITransactionResponse } from '../../IApiResponses';
import { IAddressAttributes } from '../../../domain/entities/Address';
import { DELIVERY } from '../../../routes';
import { enhancePaymentName } from './paymentNameEnhance';
import PAYMENT_PROVIDERS from '../../../constants/PaymentProviders';
import SHIPPING_METHODS from '../../../constants/ShippingMethods';
import { ITransactionPayment } from '../../../domain/entities/Transaction/Transaction';
import ConvenienceStore from '../../../domain/entities/ConvenienceStore/ConvenienceStore';
import { CONVENIENCE_STORE } from '../../../domain/entities/Config';

// mappers
import addressMapper from './addressMapper';
import cartItemsMapper from './cartItemsMapper';
import deliveryOptionsMapper from './deliveryOptionsMapper';
import paymentOptionsMapper from './paymentOptionsMapper';
import salonMapper from './salonMapper';
import orderSummaryMapper from './orderSummaryMapper';
import notificationsMapper from './notificationsMapper';
import savedCreditCardsMapper from './savedCreditCardsMapper';
import transactionPaymentsMapper from './transactionPaymentMapper';
import bopisInfoMapper from './bopisInfoMapper';

function formatCompanyBillingDetails(
  companyBillingData: ICompanyBillingDetailsResponse,
): IFromCompanyBillingDetailsResponse {
  return {
    ...companyBillingData,
    bill_to_company: !!Number(companyBillingData.bill_to_company),
  };
}

function formatInvoiceDetails(data: IInvoiceDetailsResponse): IInvoiceDetails {
  return {
    ...data,
    corporate_name: data.corporate_name || '',
    identification_type: Number(data.identification_type) as 1 | 2,
    invoice_address: data.invoice_address || '',
    invoice_carrier_id: data.invoice_carrier_id || '',
    invoice_carrier_type: data.invoice_carrier_type || INVOICE_CARRIER_TYPES.NONE,
    invoice_donate_mark: data.invoice_donate_mark || INVOICE_DONATE_MARKS.NONE,
    invoice_donation_love_code: data.invoice_donation_love_code || '',
    invoice_donation_name: data.invoice_donation_name || '',
    invoice_with_shipping: Number(data.invoice_with_shipping) as 0 | 1,
  };
}

function formatConvenienceStoreInfo(
  convenienceStoreResp: IConvenienceStoreResponse,
): ConvenienceStore {
  const {
    address, name, phone, postcode, type, id,
  } = convenienceStoreResp;
  return {
    fullAddress: address,
    isReady: true,
    phoneNumber: phone,
    postalCode: postcode,
    storeId: id,
    storeName: name,
    storeType: type || CONVENIENCE_STORE.SAGAWA,
  };
}

const transactionMapper = {
  response(resp: ITransactionResponse): IFromTransactionMapping {
    const {
      cart_items: transCartItems,
      named_delivery_date_time: {
        delivery_date: deliveryDate,
        delivery_time: deliveryTime,
      } = {},
      notifications,
      order_summary: transOrderSummary,
      offers_applied: appliedOffers,
      user,
      user: {
        email_address: guestUserEmail,
        is_guest_checkout: isGuestUser,
        saved_cc_payments: savedCreditCardsItems,
        loyalty_active_flag: userHasLoyaltyEnabled,
      },
      trans_order: {
        trans_id: transId,
        trans_order_id: transOrderId,
        loyaltyPoints,
        card_message: cardMessage,
        giftWrap: giftWrap,
      },
      trans_key: transKey,
      trans_payments,
      payment_options: paymentOptions,
      order_ship_method,
      salon,
      errors,
      bopis,
      convenience_store,
      bill_to_company,
      installments,
      delivery_tabs_disabled,
      is_POA,
      v2_delivery_options,
      is_final_sale,
    } = resp;
    // shipping address
    let address;
    let userAddresses: IAddressAttributes[] = [];
    let userShippingAddresses: IAddressAttributes[] = [];

    const isProCustomer = !!(user.pro_info && user.pro_info.isPro);

    if (user.addresses) {
      const { addresses } = user;

      if (addresses.length) {
        userAddresses = addresses.map(userAddress => addressMapper.response(userAddress));
      }
    }
    if (user.shipping_addresses) {
      const { shipping_addresses } = user;

      if (shipping_addresses && shipping_addresses.length) {
        userShippingAddresses = shipping_addresses
          .map(userAddress => addressMapper.response(userAddress));
      }
    }

    if (resp.order_ship_address?.address) {
      const {
        order_ship_address: {
          address: shippingAddress,
        },
      } = resp;
      address = shippingAddress;
    }

    // all offers applied to total (manual and automatic)
    const allAppliedOffers = (appliedOffers && appliedOffers.length)
      ? appliedOffers.map(item => {
        const {
          offer_code: offerCode,
          confirm_message: confirmMsg,
          allow_auto_messages: allowMsg,
          ...common
        } = item;

        return {
          allowMsg,
          confirmMsg,
          offerCode,
          ...common,
        };
      })
      : [];

    const cartItems = cartItemsMapper.response({
      cartItems: transCartItems,
      transactionOrder: resp.trans_order,
    });
    const orderSummary = orderSummaryMapper.response(transOrderSummary);

    const shippingAddress = address ? addressMapper.response(address) : undefined;

    const billingAddress = trans_payments.length && trans_payments[0].billing_address
      && trans_payments[0].billing_address
      ? addressMapper.response(trans_payments[0].billing_address) : undefined;
    const isExpressFlow = trans_payments.length
      ? Boolean(trans_payments[0].is_ready_to_complete)
      : false;

    const startScreen = trans_payments && trans_payments[0]?.start_screen || DELIVERY.ROUTE;

    let selectedPayment;
    let selectedPaymentMode;
    const giftCards: GiftCard[] = [];
    trans_payments.forEach((p: ITransPaymentResponse) => {
      if (p.pp_name !== CONSTANTS.GIFT_CARD) {
        selectedPayment = enhancePaymentName(p.pp_name, p.payment_mode, p.payment_type);
        selectedPaymentMode = p.payment_mode;
      } else {
        giftCards.push({
          amount: p.payment_amount,
          balance: p.balance,
          id: p.trans_payment_id,
          number: p.gc_giftcard_number,
        });
      }
    });
    const isAfterpayEligible = paymentOptions
      ? paymentOptions.some(
        p => p.pp_name === PAYMENT_PROVIDERS.AFTERPAY
          && Boolean(p.is_available),
      )
      : false;

    const isAfterpay = paymentOptions
      ? paymentOptions.some(
        p => p.pp_name === PAYMENT_PROVIDERS.AFTERPAY,
      )
      : false;

    let afterpayExcludedProducts = false;

    if (isAfterpay) {
      const afterPay = paymentOptions.find(p => p.pp_name === PAYMENT_PROVIDERS.AFTERPAY);
      afterpayExcludedProducts = afterPay?.key.some(k => k === 'after_pay_error_excluded_products') || false;
    }

    // user
    let userEmail;
    let userId;

    // TODO: (user.user) is deprecated. Remove when RCO-1901 is in production
    const userEntity: ITransactionResponse['user'] = user.user || user;

    if (!userEntity.is_guest_checkout) {
      const {
        email_address: registeredUserEmail,
        user_id,
      } = userEntity;
      userEmail = registeredUserEmail;
      userId = user_id;
    } else {
      userEmail = guestUserEmail;
    }
    const registered = userEntity && userEntity.registered;
    const recognized = userEntity && userEntity.recognized;

    const notificationsItems: INotification[] = notificationsMapper.response(notifications);
    const savedCreditCards: ICreditCard[] = savedCreditCardsMapper.response(savedCreditCardsItems || []);
    const transactionPayments: Partial<ITransactionPayment>[] = transactionPaymentsMapper.response(trans_payments);

    let bopisInfo: IFromBopisInfoResponse;
    if (bopis && bopis.bopis_is_bopis_order) {
      bopisInfo = bopisInfoMapper.response(resp as unknown as IBopisInfoResponse);
    }

    let convenienceStore;
    if (convenience_store) {
      convenienceStore = formatConvenienceStoreInfo(convenience_store);
    }

    let billToCompany;
    let invoiceDetails: IInvoiceDetails | undefined;
    if (bill_to_company) {
      if (FIELD_TYPE.INVOICE_DONATE_MARK in bill_to_company) {
        invoiceDetails = formatInvoiceDetails(bill_to_company as IInvoiceDetailsResponse);
      } else {
        billToCompany = formatCompanyBillingDetails(bill_to_company as ICompanyBillingDetailsResponse);
      }
    }

    const paymentProviders = paymentOptionsMapper.response(paymentOptions);
    const acceptedInstallments: IAcceptedInstallments[] = [];
    if (installments && installments.length) {
      installments.forEach((installment: IAcceptedInstallments) => {
        acceptedInstallments.push(installment);
      });
    }
    const userSalon = salon && salonMapper.response(salon);

    const giftMessageSettings = {
      giftMessageCharLimit: resp.gift_message?.char_limit,
      giftMessageEnabled: !!resp.gift_message?.enabled,
      giftMessagePrice: resp.gift_message?.gm_sku_price,
    };

    const giftWrapSettings = {
      giftWrapDiscountPrice: resp.gift_wrap?.gw_sku_discount_price,
      giftWrapEnabled: !!resp.gift_wrap?.enabled,
      giftWrapPrice: resp.gift_wrap?.gw_sku_price,
      giftWrapSelectedSku: resp.gift_wrap?.gw_selected_sku,
      giftWrapSkus : resp.gift_wrap?.gw_skus,
      isDefaultGWSelected: !!resp.gift_wrap?.default_gw_selected,
      isGWImageHiddenInShoppingBag: !!resp.gift_wrap?.hide_shopping_bag_gw_sku,
      isGiftWrapImageHidden: !!resp.gift_wrap?.hide_gw_image,
      isGiftWrapMultipleEnabled: !!resp.gift_wrap?.multiple_giftwraps,
    };

    const deliveryOptions = deliveryOptionsMapper.response(v2_delivery_options || []);

    return {
      acceptedInstallments,
      afterpayExcludedProducts,
      allAppliedOffers,
      billToCompany,
      billingAddress,
      bopisInfo,
      cardMessage,
      convenienceStore,
      deliveryDate,
      deliveryOptions,
      deliveryTabsDisabled: !!delivery_tabs_disabled,
      deliveryTime,
      errors,
      firstName: user.first_name,
      giftCards,
      giftMessageSettings,
      giftWrap: !!giftWrap,
      giftWrapSettings,
      invoiceDetails,
      isAfterpayEligible,
      isExpressFlow,
      isFinalSale: !!is_final_sale,
      isGuestUser,
      isKeptSignedIn: user.iskeptsignedin,
      isPOA: !!is_POA,
      isProCustomer: !!isProCustomer,
      lastName: user.last_name,
      loyaltyPoints,
      nationalId: String(user.national_id),
      notifications: notificationsItems,
      order: {
        cartItems,
        transOrderId,
      },
      orderSummary,
      paymentProviders,
      recognized,
      registered,
      savedCreditCards,
      selectedPayment,
      selectedPaymentMode,
      selectedShippingMethodId: (Number(order_ship_method) as SHIPPING_METHODS),
      shippingAddress,
      startScreen,
      transId,
      transKey,
      transactionPayments,
      userAddresses,
      userEmail,
      userHasLoyaltyEnabled: !!userHasLoyaltyEnabled,
      userId,
      userSalon,
      userShippingAddresses,
    };
  },
};

export default transactionMapper;
